<template>
  <AGCard class="pa-0" has-rounded-top>
    <div class="ag-dropzone">
      <div class="ag-dropzone__title">Envio de arquivos</div>
      <hr />
      <div
        class="ag-dropzone__content"
        :class="{ 'ag-dropzone__content--disabled': isDisabled, 'ag-dropzone__content--error': hasError }"
        @click="onClick"
        @dragover.prevent="onDragOver"
        @dragleave="onDragLeave"
        @drop.prevent="onDrop"
      >
        <div>
          <AGIcon icon="md:drive_folder_upload" size="48px" color="#442D96" />
        </div>
        <div v-if="isLimitUpload" class="ag-dropzone__message">
          <strong>Selecione até {{ limitUpload }} arquivos para envio simultâneos</strong>
          ou arraste e solte eles aqui
        </div>
        <div v-else class="ag-dropzone__message">
          <strong>Aguarde o término do upload dos arquivos anteriores</strong>
        </div>
        <div v-if="isLimitUpload" class="ag-dropzone__format-message">
          Formato .zip (Máximo de {{ maxFileSizeText }})
        </div>
      </div>

      <div v-if="hasError" class="ag-dropzone__error-message">
        <AGIcon icon="mdi-alert-circle" size="16px" />
        {{ errorMessage }}
      </div>

      <AGCard v-if="hasSelectedFiles" class="ag-dropzone__selected-files">
        <div
          v-for="(file, index) in selectedFiles"
          :key="index + file.status"
          class="ag-dropzone__selected-files--file"
        >
          <v-container>
            <v-row>
              <v-col cols="6">
                <div class="d-flex align-center">
                  <div class="d-flex">
                    <AGIcon icon="md:description" class="pa-4" size="24px" color="#442D96" />
                  </div>
                  <div class="d-flex flex-column">
                    <div class="ag-dropzone__selected-files--file-name">
                      {{ file.name }}
                    </div>
                    <div class="ag-dropzone__selected-files--file-size">Tamanho: {{ file.size }}</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="6">
                <span v-if="file.status === 'uploading'" class="ag-dropzone__selected-files--status-title"
                  >Enviando arquivo ("{{ file.progress }}%")</span
                >
                <span v-else-if="file.status === 'completed'" class="ag-dropzone__selected-files--status-title"
                  >Envio concluído ("100%")</span
                >
                <span v-else-if="file.status === 'error'" class="ag-dropzone__selected-files--status-title"
                  >Erro no envio do arquivo</span
                >

                <div v-if="file.status !== 'error'" class="d-flex align-center justify-end">
                  <v-progress-linear class="my-2" color="#442D96" height="16" rounded :model-value="file.progress" />
                  <template v-if="file.status === 'uploading'">
                    <AGButtonIcon
                      icon="md:delete"
                      size="sm"
                      color="#442D96"
                      tooltip="Deletar"
                      @click="onDeleteFile(file)"
                    />
                  </template>
                </div>
                <div v-else class="d-flex align-center justify-end">
                  <v-progress-linear class="my-2" color="#C73C44" height="16" rounded :model-value="100" />
                  <AGButtonIcon
                    icon="md:delete"
                    size="sm"
                    color="#442D96"
                    tooltip="Deletar"
                    @click="onDeleteFile(file)"
                  />
                </div>
                <div class="ag-dropzone__selected-files--file-status">
                  <span v-if="file.status === 'uploading'"> Aguarde, o arquivo está sendo enviado. </span>
                  <span
                    v-else-if="file.status === 'completed'"
                    class="d-flex align-center text-feedback-information-flourescent"
                  >
                    <AGIcon icon="mdi-check-circle-outline" size="16px" class="mr-1" />
                    Enviado com sucesso
                  </span>
                  <span v-else-if="file.status === 'error'" class="d-flex align-center text-feedback-error-dark">
                    <AGIcon icon="mdi-alert-circle" size="16px" color="#C73C44" class="mr-1" />
                    {{ file.errorMessage }}
                  </span>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </div>
      </AGCard>

      <input ref="fileInput" type="file" name="file" multiple hidden :accept="accept" @change="onFileChange" />
    </div>
  </AGCard>
</template>

<script>
import AGButtonIcon from '@/components/common/AGButtonIcon.vue';
import AGCard from '@/components/common/AGCard.vue';
import AGIcon from '@/components/common/AGIcon.vue';
import ModelArquivoUpload from '@/model/common/ModelArquivoUpload';
import { useStoreNotify } from '@/stores/StoreNotify';
import { mapActions } from 'pinia';

export default {
  emits: ['completed'],

  components: {
    AGButtonIcon,
    AGCard,
    AGIcon,
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    maxFileSize: {
      type: Number,
      default: 1048576,
    },
    maxFiles: {
      type: Number,
      default: 5,
    },
    accept: {
      type: String,
      default: 'application/zip',
    },
    url: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isDragging: false,
      selectedFiles: [],
      hasError: false,
      errorMessage: '',
    };
  },

  computed: {
    hasSelectedFiles() {
      return this.selectedFiles.length > 0;
    },

    maxFileSizeText() {
      if (this.maxFileSize >= 1024 * 1024 * 1024) {
        return (this.maxFileSize / (1024 * 1024 * 1024)).toFixed(0) + 'Gb';
      }
      if (this.maxFileSize >= 1024 * 1024) {
        return (this.maxFileSize / (1024 * 1024)).toFixed(0) + 'Mb';
      }
      return (this.maxFileSize / 1024).toFixed(2) + 'Kb';
    },

    isUploading() {
      return this.selectedFiles.some((f) => f.status === 'uploading');
    },

    limitUpload() {
      const filesCount = this.maxFiles - this.selectedFiles.filter((file) => file.status === 'uploading').length;

      if (filesCount <= 0) {
        return 0;
      }

      return filesCount;
    },

    isLimitUpload() {
      return this.limitUpload > 0;
    },

    isDisabled() {
      return this.disabled || !this.isLimitUpload;
    },
  },

  methods: {
    ...mapActions(useStoreNotify, ['notifyError']),

    onDragOver(event) {
      event.preventDefault();
      this.isDragging = true;
    },

    onDragLeave() {
      this.isDragging = false;
    },

    onClick() {
      if (this.isDisabled) return;
      this.$refs.fileInput.click();
    },

    onDrop(e) {
      e.preventDefault();
      if (this.isDisabled) return;
      const files = Array.from(e.dataTransfer.files);
      this.processaArquivos(files);
    },

    onFileChange(event) {
      const files = Array.from(event.target.files);
      this.processaArquivos(files);
    },

    processaArquivos(arquivos) {
      this.hasError = false;
      const totalFiles = arquivos.length;
      const isLimitRangeUpload = totalFiles <= this.limitUpload;

      if (totalFiles > this.maxFiles || !isLimitRangeUpload) {
        this.notifyError({ message: `Você pode selecionar no máximo ${this.limitUpload} arquivos.` });
        this.errorMessage = `Selecione no máximo ${this.limitUpload} arquivos.`;
        this.hasError = true;
        return;
      }

      arquivos.forEach((file) => {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();
        const modelArquivo = new ModelArquivoUpload(file, xhr);

        this.selectedFiles.push(modelArquivo);

        modelArquivo.onUpdate(() => {
          this.selectedFiles = [...this.selectedFiles];
        });

        if (file.size > this.maxFileSize) {
          modelArquivo.status = 'error';
          modelArquivo.errorMessage = 'Tamanho excedido';
          return;
        }

        const acceptedMimeTypes = this.accept.split(',');
        if (!acceptedMimeTypes.includes(modelArquivo.file.type)) {
          modelArquivo.status = 'error';
          modelArquivo.errorMessage = 'Tipo de arquivo não permitido';
          return;
        }

        modelArquivo.xhr.addEventListener('readystatechange', () => {
          if (modelArquivo.xhr.readyState !== 4) return;
          if (modelArquivo.xhr.status === 200) {
            modelArquivo.status = 'completed';
          } else if (modelArquivo.xhr.status !== 200) {
            modelArquivo.status = 'error';
            if (modelArquivo.xhr.status === 409) {
              modelArquivo.errorMessage = 'Arquivo já existe';
            } else {
              modelArquivo.errorMessage = 'Ocorreu um erro';
            }
          }
          this.selectedFiles = [...this.selectedFiles];
          this.$emit('completed');
        });

        modelArquivo.xhr.open('POST', this.url, true);
        modelArquivo.xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('access_Token')}`);
        modelArquivo.xhr.setRequestHeader('ipAddress', localStorage.getItem('ipAddress'));
        formData.append('file', file);
        modelArquivo.xhr.send(formData);

        modelArquivo.status = 'uploading';
        this.selectedFiles = [...this.selectedFiles];
      });
    },

    onDeleteFile(fileToDelete) {
      if (fileToDelete.xhr) {
        fileToDelete.xhr.abort();
      }

      this.selectedFiles = this.selectedFiles.filter((file) => file.id !== fileToDelete.id);

      this.updateFileInput();
    },

    updateFileInput() {
      if (!this.$refs.fileInput) return;
      const dataTransfer = new DataTransfer();

      this.selectedFiles.forEach((file) => {
        if (file.file) {
          dataTransfer.items.add(file.file);
        }
      });

      this.$refs.fileInput.files = dataTransfer.files;
    },
  },
};
</script>

<style scoped lang="scss">
.ag-dropzone {
  .ag-dropzone__title {
    padding: 16px;
    font-size: $font-size-sm;
    font-weight: $font-weight-bold;
  }
  .ag-dropzone__content {
    min-width: 650px;
    margin: 16px;
    padding: 64px 16px;
    border: 2px dashed $neutral-color-low-dark;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;

    &:hover {
      background-color: $brand-color-highlight-light;
      cursor: pointer;
    }

    &:active {
      border-color: $brand-color-highlight-medium;
      background-color: $neutral-color-low-medium;
    }

    &--disabled {
      background-color: $neutral-color-low-dark;
      cursor: not-allowed !important;
      opacity: 0.5;
    }

    &--error {
      border: 1px solid $feedback-color-error-dark;
      background-color: $feedback-color-error-light;
      &:hover {
        background-color: $feedback-color-error-light;
      }
    }

    .ag-dropzone__message {
      text-align: center;
      color: $brand-color-primary-dark;
      font-size: $font-size-xs;
    }

    .ag-dropzone__format-message {
      color: $neutral-color-high-medium;
      font-size: $font-size-xxs;
    }
  }

  .ag-dropzone__error-message {
    color: $feedback-color-error-medium;
    font-size: $font-size-xxs;
    padding: 0 16px 16px;
  }

  .ag-dropzone__selected-files {
    width: auto !important;
    margin: 16px;

    .ag-dropzone__selected-files--file {
      background-color: $neutral-color-low-medium;
      border-radius: $border-radius-sm;
      margin-bottom: 16px;

      .ag-dropzone__selected-files--file-name {
        word-break: break-word;
        font-weight: $font-weight-bold;
        font-size: $font-size-xs;
        color: $neutral-color-high-dark;
      }
      .ag-dropzone__selected-files--file-size {
        color: $neutral-color-high-medium;
        font-size: $font-size-xxs;
      }
      .ag-dropzone__selected-files--status-title {
        color: $brand-color-primary-dark;
        font-size: $font-size-xs;
      }
      .ag-dropzone__selected-files--file-status {
        font-size: $font-size-xxxs;
      }
    }
  }
}
</style>
